import React, {
  useContext,
  useEffect,
  useState,
  Suspense,
  useRef,
  useMemo,
  useCallback,
} from "react";
import styled from "styled-components";
import {
  List,
  FlexboxGrid,
  Loader,
  Message,
  Whisper,
  Popover,
  Checkbox,
} from "rsuite";
import { GlassCard } from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";
import { shadows } from "../../../../ui/theme/shadows";
import "rsuite/dist/rsuite.min.css";
import { DarkModeContext } from "../../../../../appContexts/DarkModeContext";
import { useHistory } from "react-router-dom";
import { renderCount } from "../../../../../appHelpers/appLoggers";
import LocationIcon from "@rsuite/icons/Location";
import { LastReadFC } from "./_helpers/dashboardWidgets/LastReadFC";
import { RecentValuesFC } from "./_helpers/dashboardWidgets/RecentValuesFC";
import { appTheme } from "../../../../../appHelpers/appTheme";
import { _FilterEquipmentFC } from "./_helpers/dashboardFilters/_FilterEquipmentFC";
import { MachinesContext } from "./_context/MachinesContext";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import LightSparkline from "./_helpers/dashboardWidgets/LightSparklineFC";
import NoticeIcon from "@rsuite/icons/Notice";
import { AlertText } from "../../_utils/util_general";
import { UserContext } from "../../_context/UserContext";
import { AlertListFC } from "./_helpers/AlertListFC";
import { DateTime, Interval } from "luxon";
import TicketBadge from "../../../../tickets/TicketBadge";

export const MachinesTableFC = ({
  machines,
  alertConfigs,
  networkIssues,
  dashboardAlerts,
}) => {
  // renderCount('MachinesTableFC');
  const history = useHistory();
  const [displayMachines, setDisplayMachines] = useState(machines);
  const [displayCustomers, setDisplayCustomers] = useState([]);
  const [displayFileDatetimes, setDisplayFileDatetimes] = useState([]);
  const { darkMode } = useContext(DarkModeContext);
  const { user, userSiteGroups } = useContext(UserContext);
  const clickStartRef = useRef(null);

  const {
    loadingDisplayRecentValues,
    loadingDisplaySparklineData,
    displayRecentValues,
    displaySparklineData,
    getRecentValues,
    getSparklineData,
    frozenSystems,
  } = useContext(MachinesContext);

  const speakerElement = (system) => (
    <Popover style={{ width: "600px" }}>
      <AlertListFC user={user} system={system} />
    </Popover>
  );

  const RowHeader = () => (
    <FlexboxGrid justify="center" style={{ paddingTop: 5, paddingBottom: 5 }}>
      <FlexboxGrid.Item colspan={2} style={headerStyle}>
        <HeaderText> EquipmentID </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={4} style={headerStyle}>
        <HeaderText> Location </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={2} style={headerStyle}>
        <HeaderText> Manufacturer </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={1} style={headerStyle}>
        <HeaderText> </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle}>
        <HeaderText> Helium Q24H </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle}>
        <HeaderText> Pressure Q24H </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={5} style={{ ...headerStyle, paddingLeft: "44px" }}>
        <HeaderText>Recent Parameters</HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={4} style={headerStyle}>
        <HeaderText> DateTime </HeaderText>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );

  useEffect(() => {
    getRecentValues();
    getSparklineData();
  }, [machines]);

  useEffect(() => {
    setDisplayCustomers([
      ...new Set(displayMachines?.map((item) => item.customer_name)),
    ]);
  }, [displayMachines]);

  const handleSiteClick = (siteName, customerName) => {
    // Navigate to a site-specific view - you can customize this URL pattern
    history.push(
      `/customer/${encodeURIComponent(customerName)}/site/${encodeURIComponent(siteName)}`,
    );
  };

  // Memoize grouped systems to avoid recalculating on every render
  const groupedSystemsByCustomer = useMemo(() => {
    const cache = {};

    displayCustomers.forEach((customerName) => {
      const customerSystems = displayMachines?.filter(
        (item) => item.customer_name === customerName,
      );
      const result = [];

      // Get all site groups the user has access to for this customer
      const userCustomerSiteGroups =
        userSiteGroups?.filter(
          (userGroup) => userGroup.customer_name === customerName,
        ) || [];

      // Create set of sites that are in user's AUTHORIZED site groups
      const sitesInAuthorizedGroups = new Set();

      // Only process if user has explicit site group access
      if (userCustomerSiteGroups.length > 0) {
        // Group user's site groups and their systems
        const siteGroupMap = {};
        userCustomerSiteGroups.forEach((userGroup) => {
          if (
            userGroup.site_group_name &&
            !siteGroupMap[userGroup.site_group_name]
          ) {
            siteGroupMap[userGroup.site_group_name] = new Set();
          }
          if (userGroup.site_name && userGroup.site_group_name) {
            siteGroupMap[userGroup.site_group_name].add(userGroup.site_name);
            sitesInAuthorizedGroups.add(userGroup.site_name);
          }
        });

        // Add systems grouped by authorized site groups first
        Object.entries(siteGroupMap).forEach(([groupName, sites]) => {
          const groupSystems = customerSystems.filter((system) =>
            sites.has(system.site_name),
          );
          if (groupSystems.length > 0) {
            result.push({
              type: "group",
              site_group_name: groupName,
              systems: groupSystems,
            });
          }
        });
      }

      // Get individual systems (sites user has access to but NOT through a site group)
      const individualSystems = customerSystems.filter(
        (system) => !sitesInAuthorizedGroups.has(system.site_name),
      );

      // Add individual systems last
      if (individualSystems.length > 0) {
        result.push({
          type: "individual",
          systems: individualSystems,
        });
      }

      cache[customerName] = result;
    });

    return cache;
  }, [displayMachines, displayCustomers, userSiteGroups]);

  const getGroupedSystemsForCustomer = useCallback(
    (customerName) => {
      return groupedSystemsByCustomer[customerName] || [];
    },
    [groupedSystemsByCustomer],
  );

  useEffect(() => {}, [loadingDisplayRecentValues]);

  return displayMachines ? (
    <MachineTableSC darkMode={darkMode}>
      {/* Filter Control Header Row */}
      <_FilterEquipmentFC
        machines={machines}
        displayMachines={displayMachines}
        setDisplayMachines={setDisplayMachines}
        recent_data={displayRecentValues}
        alertConfigs={alertConfigs}
        networkIssues={networkIssues}
        frozenSystems={frozenSystems}
        dashboardAlerts={dashboardAlerts}
      />

      {window.screen.width >= 768 ? (
        <List size="sm" hover>
          {/* Header For Equipment List */}
          {displayCustomers.map((customer, customerIndex) => (
            <CustomerCard
              key={customer}
              darkMode={darkMode}
              style={{ marginTop: customerIndex === 0 ? "16px" : undefined }}
            >
              {/* Customer Header */}
              <CustomerHeader>
                <CustomerText> {customer} </CustomerText>
              </CustomerHeader>

              {customerIndex === 0 ? <RowHeader /> : <></>}

              {(() => {
                const groupedSystems = getGroupedSystemsForCustomer(customer);
                const hasGroups = groupedSystems.some(
                  (group) => group.type === "group",
                );

                return groupedSystems.flatMap((group, groupIndex) => [
                  // Add header for site groups and individual sites (only show Individual Sites header if there are also groups)
                  group.type === "group" ||
                  (group.type === "individual" && hasGroups) ? (
                    <SiteGroupHeader
                      key={`${customer}-${group.type}-${group.site_group_name || "individual"}`}
                    >
                      <SiteGroupText darkMode={darkMode}>
                        {group.type === "group"
                          ? `${group.site_group_name} (${group.systems.length})`
                          : `Individual Sites (${group.systems.length})`}
                      </SiteGroupText>
                    </SiteGroupHeader>
                  ) : null,
                  // Add all systems in this group
                  ...group.systems.map((system) => (
                    <List.Item
                      value={system.id}
                      key={system.id}
                      index={customerIndex + 1}
                      style={{
                        backgroundColor: `${
                          displayRecentValues?.find(
                            (data) => data.system_id === system.id,
                          )?.comp_vib_status === false &&
                          alertConfigs?.filter(
                            (config) =>
                              config.system_id === system.id &&
                              config.field_name.includes("comp_vib_status") &&
                              config.user_id !== "default" &&
                              config.enabled,
                          ).length !== 0 &&
                          alertConfigs?.filter(
                            (config) =>
                              config.system_id === system.id &&
                              config.field_name.includes("comp_vib_status") &&
                              config.user_id === "default" &&
                              config.enabled,
                          ).length !== 0
                            ? "rgb(255, 242, 242)"
                            : ""
                        }`,
                      }}
                    >
                      {/* Checks whether there's an unresolved issue */}
                      {networkIssues ? (
                        networkIssues[0]?.filter(
                          (issue) =>
                            issue.id === system.id &&
                            !issue.resolved &&
                            issue.status === "active" &&
                            (issue.processor_type === "MMB" ||
                              issue.type === "MAGNET"),
                        ).length > 0 ? (
                          <Whisper
                            placement="top"
                            trigger="hover"
                            speaker={
                              <Popover style={{ width: "400px" }}>
                                <div style={{ padding: "8px" }}>
                                  <strong>Unresolved Issues:</strong>
                                  {networkIssues[0]
                                    ?.filter(
                                      (issue) =>
                                        issue.id === system.id &&
                                        !issue.resolved &&
                                        issue.status === "active" &&
                                        (issue.processor_type === "MMB" ||
                                          issue.type === "MAGNET"),
                                    )
                                    .map((issue, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          marginTop: "8px",
                                          paddingTop: "8px",
                                          borderTop:
                                            idx > 0
                                              ? "1px solid #e0e0e0"
                                              : "none",
                                        }}
                                      >
                                        <div>
                                          <strong>Type:</strong> {issue.type}
                                        </div>
                                        {issue.description && (
                                          <div style={{ marginTop: "4px" }}>
                                            <strong>Description:</strong>{" "}
                                            {issue.description}
                                          </div>
                                        )}
                                        {issue.notes && (
                                          <div style={{ marginTop: "4px" }}>
                                            <strong>Notes:</strong>{" "}
                                            {issue.notes}
                                          </div>
                                        )}
                                        {issue.created_at && (
                                          <div
                                            style={{
                                              marginTop: "4px",
                                              fontSize: "0.85em",
                                              color: "grey",
                                            }}
                                          >
                                            Reported:{" "}
                                            {DateTime.fromISO(
                                              issue.created_at,
                                            ).toFormat("LLL dd, yyyy")}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              </Popover>
                            }
                          >
                            <FlexboxGrid
                              style={{
                                backgroundColor: "#fffaf2",
                                border: `1px solid ${colors.warning.border}`,
                                borderRadius: 6,
                                paddingLeft: 10,
                                paddingTop: 2,
                                paddingBottom: 2,
                                marginBottom: 5,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <FlexboxGrid.Item
                                colspan={1}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  lineHeight: 1,
                                }}
                              >
                                <RemindFillIcon color="#ffb300" />
                              </FlexboxGrid.Item>
                              <FlexboxGrid.Item
                                colspan={23}
                                style={{ color: "grey", display: "flex", alignItems: "center" }}
                              >
                                Warning: This system has an unresolved reported
                                issue.
                              </FlexboxGrid.Item>
                            </FlexboxGrid>
                          </Whisper>
                        ) : null
                      ) : null}

                      {/* Checks whether there's an unresolved issue */}
                      {frozenSystems?.filter(
                        (frozen) => frozen.system_id === system.id,
                      ).length > 0 &&
                      alertConfigs?.filter(
                        (config) =>
                          config.system_id === system.id &&
                          config.field_name.includes("host_datetime") &&
                          config.operator.includes("frozen_host") &&
                          config.user_id !== "default" &&
                          config.enabled,
                      ).length !== 0 &&
                      alertConfigs?.filter(
                        (config) =>
                          config.system_id === system.id &&
                          config.field_name.includes("host_datetime") &&
                          config.operator.includes("frozen_host") &&
                          config.user_id === "default" &&
                          config.enabled,
                      ).length !== 0 ? (
                        <FlexboxGrid
                          style={{
                            backgroundColor: "rgb(255, 242, 242)",
                            paddingLeft: 10,
                            marginBottom: 5,
                          }}
                        >
                          <FlexboxGrid.Item colspan={1}>
                            <RemindFillIcon color="red" />
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item
                            colspan={23}
                            style={{ color: "grey" }}
                          >
                            Error: This system has instances of FROZEN data in
                            the past 6 hours.
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                      ) : null}

                      {/* Checks whether there's an unresolved issue */}
                      {displayRecentValues?.find(
                        (data) => data.system_id === system.id,
                      )?.comp_vib_status === false &&
                      alertConfigs?.filter(
                        (config) =>
                          config.system_id === system.id &&
                          config.field_name.includes("comp_vib_status") &&
                          config.user_id !== "default" &&
                          config.enabled,
                      ).length !== 0 &&
                      alertConfigs?.filter(
                        (config) =>
                          config.system_id === system.id &&
                          config.field_name.includes("comp_vib_status") &&
                          config.user_id === "default" &&
                          config.enabled,
                      ).length !== 0 ? (
                        <FlexboxGrid
                          style={{
                            backgroundColor: "rgb(255, 242, 242)",
                            paddingLeft: 10,
                          }}
                        >
                          <FlexboxGrid.Item colspan={1}>
                            <RemindFillIcon color="red" />
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item
                            colspan={23}
                            style={{ color: "grey" }}
                          >
                            EDU: Compressor appears to be off!
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                      ) : null}

                      <SystemRow
                        onMouseDown={(event) => {
                          clickStartRef.current = event.timeStamp;
                        }} // Record the timestamp of the mouse down event
                        onMouseUp={(event) => {
                          const clickEnd = event.timeStamp; // Get the timestamp of the mouse up event
                          const clickDuration =
                            clickEnd - (clickStartRef.current || 0); // Calculate the duration of the click
                          const modalElement =
                            document.getElementById("control-id-hover");

                          if (
                            clickDuration < 200 &&
                            !modalElement?.contains(event.target)
                          ) {
                            history.push(`/system/${system.id}`); // Navigate to the next page if the click was short
                          }
                        }}
                      >
                        <FlexboxGrid>
                          <FlexboxGrid.Item colspan={2} style={itemGridStyle}>
                            <ItemContent>
                              {system.cus_sys_id ? (
                                <>
                                  <ItemMainText>
                                    {" "}
                                    {system.cus_sys_id}{" "}
                                  </ItemMainText>
                                  <ItemSubText> {system.id} </ItemSubText>
                                </>
                              ) : (
                                <ItemMainText> {system.id} </ItemMainText>
                              )}
                            </ItemContent>
                            <span onMouseDown={e => e.stopPropagation()} onMouseUp={e => e.stopPropagation()}>
                              <TicketBadge systemId={system.id} />
                            </span>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={4} style={itemGridStyle}>
                            <ItemContent>
                              <ItemMainText>
                                <LocationIcon /> {system.site_name}
                              </ItemMainText>
                              {/* <ItemSubText>
                                       Room: {system.room ? system.room : 'N/A'}
                                    </ItemSubText> */}
                              <ItemSubText>
                                {system.state} · {system.city}{" "}
                              </ItemSubText>
                            </ItemContent>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={2} style={itemGridStyle}>
                            <ItemContent>
                              <ItemMainText>{system.manufacturer}</ItemMainText>
                              <ItemSubText>
                                {system.model ? system.model : "N/A"}
                              </ItemSubText>
                            </ItemContent>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={1} style={itemGridStyle}>
                            {alertConfigs?.filter(
                              (config) =>
                                config.system_id === system.id &&
                                !config.pg_table.includes("log") &&
                                config.enabled,
                            ).length !== 0 ? (
                              <Whisper
                                preventOverflow
                                placement="left"
                                trigger="hover"
                                controlId="control-id-hover"
                                speaker={speakerElement(system)}
                                enterable
                              >
                                <NoticeIcon color={appTheme.avante_dark_blue} />
                              </Whisper>
                            ) : alertConfigs?.filter(
                                (config) =>
                                  config.system_id === system.id &&
                                  !config.pg_table.includes("log"),
                              ).length !== 0 ? (
                              <Whisper
                                preventOverflow
                                placement="left"
                                trigger="hover"
                                controlId="control-id-hover"
                                speaker={speakerElement(system)}
                                enterable
                              >
                                <NoticeIcon color="grey" />
                              </Whisper>
                            ) : (
                              <NoticeIcon color="red" />
                            )}
                          </FlexboxGrid.Item>

                          {/* Sparkline Box for Helium Levels */}
                          <FlexboxGrid.Item colspan={3} style={itemChartStyle}>
                            {loadingDisplaySparklineData ? (
                              <Loader speed="fast" />
                            ) : (
                              <LightSparkline
                                type={"helium"}
                                units={
                                  displaySparklineData?.find(
                                    (data) => data.system_id === system.id,
                                  )?.he_level_units
                                }
                                data={displaySparklineData
                                  ?.find((data) => data.system_id === system.id)
                                  ?.helium_array.filter(Number)}
                                width={100}
                                height={20}
                                tank={system.he_tank_volume}
                              />
                            )}
                          </FlexboxGrid.Item>

                          {/* Sparkline Box for Pressure Levels */}
                          <FlexboxGrid.Item colspan={3} style={itemChartStyle}>
                            {loadingDisplaySparklineData ? (
                              <Loader speed="fast" />
                            ) : (
                              <LightSparkline
                                type={"pressure"}
                                units={
                                  displaySparklineData?.find(
                                    (data) => data.system_id === system.id,
                                  )?.pressure_units
                                }
                                data={displaySparklineData
                                  ?.find((data) => data.system_id === system.id)
                                  ?.pressure_array.filter(Number)}
                                width={100}
                                height={20}
                                tank={system.he_tank_volume}
                              />
                            )}
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={4} style={itemChartStyle}>
                            <ItemContent>
                              {loadingDisplayRecentValues ? (
                                <Loader speed="fast" />
                              ) : (
                                <RecentValuesFC
                                  recent_values={displayRecentValues?.find(
                                    (data) => data.system_id === system.id,
                                  )}
                                  alertConfigs={alertConfigs?.filter(
                                    (config) => config.system_id === system.id,
                                  )}
                                  tank={system.he_tank_volume}
                                />
                              )}
                            </ItemContent>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={4} style={itemChartStyle}>
                            <ItemContent>
                              <LastReadFC
                                recent_datetimes={machines.find(
                                  (data) => data.id === system.id,
                                )}
                              />
                            </ItemContent>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                      </SystemRow>
                    </List.Item>
                  )),
                ]);
              })().flat()}
            </CustomerCard>
          ))}
        </List>
      ) : (
        // MOBILE VIEW
        <List hover>
          {displayCustomers.map((customer, customerIndex) => (
            <CustomerCard
              key={customer}
              darkMode={darkMode}
              style={{ marginTop: customerIndex === 0 ? "16px" : undefined }}
            >
              {/* Customer Header */}
              <CustomerHeader>
                <CustomerText> {customer} </CustomerText>
              </CustomerHeader>

              {(() => {
                const groupedSystems = getGroupedSystemsForCustomer(customer);
                const hasGroups = groupedSystems.some(
                  (group) => group.type === "group",
                );

                return groupedSystems.flatMap((group, groupIndex) => [
                  // Add header for site groups and individual sites (only show Individual Sites header if there are also groups)
                  group.type === "group" ||
                  (group.type === "individual" && hasGroups) ? (
                    <SiteGroupHeader
                      key={`${customer}-${group.type}-${group.site_group_name || "individual"}-mobile`}
                    >
                      <SiteGroupText darkMode={darkMode}>
                        {group.type === "group"
                          ? `${group.site_group_name} (${group.systems.length})`
                          : `Individual Sites (${group.systems.length})`}
                      </SiteGroupText>
                    </SiteGroupHeader>
                  ) : null,
                  // Add all systems in this group
                  ...group.systems.map((system) => (
                    <div key={system.id}>
                      <FlexboxGrid
                        justify="center"
                        onClick={(event) => {
                          history.push(`/system/${system.id}`);
                        }}
                      >
                        <FlexboxGrid.Item
                          colspan={14}
                          style={{ textAlign: "left", justifyContent: "left" }}
                        >
                          <LocationHeader>
                            <ItemMainText>
                              {" "}
                              {system.site_name &&
                              system.site_name !== "undefined" ? (
                                <>
                                  <LocationIcon /> {system.site_name}
                                </>
                              ) : (
                                <></>
                              )}
                            </ItemMainText>
                          </LocationHeader>
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={10}
                          style={{
                            ...itemGridStyleLocation,
                            textAlign: "right",
                            justifyContent: "right",
                          }}
                        >
                          <LastReadFC
                            recent_datetimes={machines.find(
                              (data) => data.id === system.id,
                            )}
                          />
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={24}
                          style={{
                            ...itemGridStyleLocation,
                            textAlign: "right",
                            justifyContent: "right",
                          }}
                        >
                          <hr
                            style={{
                              margin: 0,
                              padding: 0,
                              marginTop: "0%",
                              paddingBottom: "1%",
                              width: "95vw",
                            }}
                          />
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={6}
                          style={{
                            ...itemGridStyle,
                            textAlign: "left",
                            justifyContent: "left",
                          }}
                        >
                          <ItemContent>
                            {system.cus_sys_id ? (
                              <div>
                                <ItemMainText>
                                  {" "}
                                  {system.cus_sys_id}{" "}
                                </ItemMainText>
                                <ItemSubText> {system.id} </ItemSubText>
                              </div>
                            ) : (
                              <ItemMainText> {system.id} </ItemMainText>
                            )}
                            <div>
                              {system.manufacturer ? (
                                <ValueBox>
                                  {" "}
                                  {`${system.manufacturer} - ${system.modality}`}
                                </ValueBox>
                              ) : (
                                <></>
                              )}
                              {system.model ? (
                                <ValueBox>
                                  <ItemSubText>{system.model}</ItemSubText>
                                </ValueBox>
                              ) : (
                                <></>
                              )}
                            </div>
                            <span onClick={e => e.stopPropagation()} style={{ marginTop: '4px' }}>
                              <TicketBadge systemId={system.id} />
                            </span>
                          </ItemContent>
                        </FlexboxGrid.Item>

                        {/* Sparkline Box for Helium Levels */}
                        <FlexboxGrid.Item
                          colspan={9}
                          style={{
                            ...itemChartStyle,
                            justifyContent: "center",
                          }}
                        >
                          <ItemContent>
                            {loadingDisplaySparklineData ? (
                              <Loader speed="fast" />
                            ) : (
                              <LightSparkline
                                type={"helium"}
                                units={
                                  displaySparklineData?.find(
                                    (data) => data.system_id === system.id,
                                  )?.he_level_units
                                }
                                data={displaySparklineData
                                  ?.find((data) => data.system_id === system.id)
                                  ?.helium_array.filter(Number)}
                                width={120}
                                height={25}
                              />
                            )}
                          </ItemContent>
                        </FlexboxGrid.Item>

                        {/* Sparkline Box for Pressure Levels */}
                        <FlexboxGrid.Item
                          colspan={9}
                          style={{
                            ...itemChartStyle,
                            justifyContent: "center",
                          }}
                        >
                          <ItemContent>
                            {loadingDisplaySparklineData ? (
                              <Loader speed="fast" />
                            ) : (
                              <LightSparkline
                                type={"pressure"}
                                units={
                                  displaySparklineData?.find(
                                    (data) => data.system_id === system.id,
                                  )?.pressure_units
                                }
                                data={displaySparklineData
                                  ?.find((data) => data.system_id === system.id)
                                  ?.pressure_array.filter(Number)}
                                width={120}
                                height={25}
                              />
                            )}
                          </ItemContent>
                        </FlexboxGrid.Item>
                      </FlexboxGrid>

                      <hr
                        style={{
                          margin: 0,
                          padding: 0,
                          marginTop: "2%",
                          paddingBottom: "2%",
                          width: "95vw",
                        }}
                      />
                    </div>
                  )),
                ]);
              })().flat()}
            </CustomerCard>
          ))}
        </List>
      )}
    </MachineTableSC>
  ) : (
    <></>
  );
};

const MachineTableSC = styled.div`
  width: 96vw;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};

  .rs-list-item {
    background-color: ${({ theme, darkMode }) =>
      darkMode ? theme.dark_primary : theme.light_primary};
  }

  padding-bottom: 50px;

  @media screen and (min-width: 769px) {
    width: 94vw;
  }
`;

const ItemContent = styled.div`
  display: block;
  overflow: hidden;
  text-align: left;
`;

const ItemMainText = styled.div`
  font-size: 0.9em;
  font-weight: 500;
  line-height: 1.3;

  @media screen and (max-width: 769px) {
    margin-top: 4px;
    padding: 0;
    font-size: 0.9em;
    font-weight: bolder;
  }
`;

const ItemSubText = styled.div`
  font-size: 0.7em;
  color: #97969b;
  font-weight: normal;
  line-height: 1.2;

  @media screen and (max-width: 769px) {
    font-size: 0.8em;
    font-weight: normal;
  }
`;

const HeaderText = styled.div`
  font-size: 0.8em;
  color: #97969b;
`;

const LocationHeader = styled.div`
  font-size: 0.75em;
`;

const CustomerText = styled.div`
  font-size: 1em;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;

  @media screen and (max-width: 769px) {
    font-size: 0.85em;
    letter-spacing: 0.5px;
  }
`;

const SiteGroupText = styled.div`
  font-size: 0.8em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;

  @media screen and (max-width: 769px) {
    font-size: 0.75em;
    letter-spacing: 0.4px;
  }
`;

const headerStyle = {
  display: "flex",
  paddingLeft: "1%",
  paddingRight: "1%",
  flexDirection: "column",
  alignItems: "flex-start",
  overflow: "hidden",
};

const itemGridStyle = {
  display: "flex",
  alignItems: "center",
  height: "50px",
  paddingLeft: "0.5%",
  paddingRight: "0.5%",
};

const itemGridStyleLocation = {
  display: "block",
  alignItems: "center",
  paddingBottom: "1%",
  paddingLeft: "1%",
};

const itemChartStyle = {
  display: "grid",
  alignItems: "center",
  height: "50px",
  paddingLeft: "0.5%",
  paddingRight: "0.5%",
  flexDirection: "column",
};

const ValueBox = styled.div`
  font-size: 0.7em;
  overflow: hidden;
  display: inline;

  @media screen and (max-width: 769px) {
    display: block;
  }
`;

const CustomerCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  margin-bottom: 16px;
  padding: 12px;
  box-shadow: ${shadows.sm};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.3s ease;

  @media screen and (max-width: 769px) {
    padding: 12px;
    margin-bottom: 16px;
  }
`;

const SystemRow = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 4px;
  padding: 8px 12px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 769px) {
    padding: 16px 12px;
    margin-bottom: 8px;
  }
`;

const SiteGroupHeader = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 3px solid ${colors.primary.main};
  border-radius: 6px;
  margin: 6px -12px 8px -12px;
  padding: 8px 12px;
  box-shadow: ${shadows.sm};
`;

const CustomerHeader = styled.div`
  background: ${colors.primary.main};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px 12px 0 0;
  margin: -12px -12px 12px -12px;
  padding: 10px 16px;
  box-shadow: ${shadows.sm};
  display: flex;
  justify-content: center;
  align-items: center;
`;
